import React, { useState, useRef, ChangeEventHandler } from 'react';
import theme from './ProjectSettingsDeadlineAlerts.module.scss';
import mainTheme from '../ProjectSettings.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput } from '@/components/forms/validation';
import { useAsyncState } from '@/utils/hooks';
import { getProjectDeadlineAlerts, saveProjectDeadlineAlerts } from '@/services/projects';
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { ProjectDeadlineAlert, TranslatableText, DeadlineAlertType } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { toNullableNumber } from '@/utils/html';
import { AlertBanner, Button, ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

interface ColumnProps {
    title: TranslatableText;
    tooltip?: TranslatableText;
    checked: boolean;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    showHeader: boolean;
}

const Column: React.FC<ColumnProps> = ({ title, tooltip, checked, onChange, showHeader }) => {
    return (
        <div className={theme.column}>
            {showHeader && (
                <span>
                    {title}
                    {tooltip && <InfoIconWithTooltip content={tooltip} />}
                </span>
            )}
            <SlideToggle onChange={onChange} checked={checked} />
        </div>
    );
};
interface ProjectSettingsDeadlineAlertsProps {
    projectId: number;
}

const ProjectSettingsDeadlineAlerts: React.FC<ProjectSettingsDeadlineAlertsProps> = ({ projectId }) => {
    const [deadlineAlerts, setDeadlineAlerts] = useAsyncState(() => getProjectDeadlineAlerts(projectId), [], []);
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (deadlineAlert: ProjectDeadlineAlert, changes: Partial<ProjectDeadlineAlert>) => {
        setDeadlineAlerts((oldValue) => arrayReplace(oldValue, deadlineAlert, { ...deadlineAlert, ...changes }));
    };

    const handleDelete = (deadlineAlert: ProjectDeadlineAlert) => {
        setDeadlineAlerts((oldValue) => oldValue.filter((x) => x !== deadlineAlert));
    };

    const handleAdd = () => {
        setDeadlineAlerts((oldValue) => [
            ...oldValue,
            {
                alertType: DeadlineAlertType.BeforeDeadline,
                daysBeforeDeadline: 1,
                sendEmail: false,
                sendInApp: true,
                sendSms: false,
                id: --newItemCounter.current,
            },
        ]);
    };

    const newItemCounter = useRef(0);

    const onSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const fixedDeadlines = deadlineAlerts.map((dl) => {
                return {
                    ...dl,
                    // We're hiding option for InApp. And always enabling it for now.
                    sendInApp: true,
                    id: dl.id <= 0 ? undefined : dl.id,
                };
            });
            const updatedDeadlineAlerts = await saveProjectDeadlineAlerts(projectId, fixedDeadlines);
            setDeadlineAlerts(updatedDeadlineAlerts);
            Toast.success(<Trans>Deadline alerts saved</Trans>);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <AlertBanner type="info">
                <Trans>
                    <p className={theme.bannerParagraph}>
                        Creating a deadline alert allows you to specify when buyers will be notified about an upcoming
                        deadline.
                    </p>
                    <strong>Please note,</strong> a deadline alert connected to options will only send a notification to
                    buyers who have unsigned options. <br /> Whereas, a deadline alert not connected to options will send a
                    notification to all buyers as the deadline approaches.
                </Trans>
            </AlertBanner>
            <div className={mainTheme.section}>
                <form onSubmit={onSave}>
                    <div>
                        {deadlineAlerts.map((dla, index) => (
                            <div className={theme.row} key={dla.id}>
                                <Column
                                    showHeader={index === 0}
                                    title={<Trans>Email</Trans>}
                                    checked={dla.sendEmail}
                                    onChange={(e) => handleChange(dla, { sendEmail: e.target.checked })}
                                />
                                <Column
                                    showHeader={index === 0}
                                    title={<Trans>SMS</Trans>}
                                    checked={dla.sendSms}
                                    onChange={(e) => handleChange(dla, { sendSms: e.target.checked })}
                                />
                                {/* <Column
                                showHeader={index === 0}
                                title={<Trans>In-app alerts</Trans>}
                                checked={dla.sendInApp}
                                onChange={() => handleChange(dla, 'sendInApp', !dla.sendInApp)}
                            /> */}
                                <div className={theme.inputColumn}>
                                    {index === 0 && (
                                        <span>
                                            <Trans>Send alert</Trans>
                                            <InfoIconWithTooltip
                                                content={
                                                    <Trans>
                                                        The number of days before the deadline the alert will be sent.
                                                    </Trans>
                                                }
                                            />
                                        </span>
                                    )}
                                    <div>
                                        <ValidatingInput
                                            className="form-control"
                                            onChange={(e) =>
                                                handleChange(dla, { daysBeforeDeadline: toNullableNumber(e.target.value)! })
                                            }
                                            type="number"
                                            value={dla.daysBeforeDeadline}
                                            min={0}
                                        />
                                        <select
                                            className="form-control"
                                            value={dla.alertType}
                                            onChange={(e) =>
                                                handleChange(dla, { alertType: DeadlineAlertType[e.target.value] })
                                            }>
                                            <option
                                                value={DeadlineAlertType.BeforeDeadline}>{t`days before deadline`}</option>
                                            <option value={DeadlineAlertType.AfterDeadline}>{t`days after deadline`}</option>
                                        </select>

                                        {dla.alertType === DeadlineAlertType.AfterDeadline && (
                                            <span>
                                                <Trans>
                                                    Notifications will only be sent to buyers with unsigned options
                                                </Trans>
                                            </span>
                                        )}
                                        {/* <span>
                                            <Trans>days before deadline</Trans>
                                        </span> */}
                                        <Button
                                            variant="danger"
                                            className={theme.trashButton}
                                            onClick={() => handleDelete(dla)}>
                                            <FontAwesomeIcon size="lg" icon={faTrashAlt} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <ButtonBar>
                        <Button icon={faPlus} variant="tertiary" onClick={handleAdd}>
                            <Trans>Add deadline alert</Trans>
                        </Button>
                        <ButtonBar.Right>
                            <Button className={mainTheme.saveButton} type="submit" variant="primary" loading={isSaving}>
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </form>
            </div>
        </>
    );
};

export default ProjectSettingsDeadlineAlerts;
